import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { createAdCampaign, patchAdCampaign, startAdCampaign, stopAdCampaign } from 'apis/ad-bookings/api';
import { AdCampaignResponse } from 'apis/ad-bookings/types';
import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';

import { useCampaignsRoutes } from './useCampaignsRoutes';
import { CampaignsQueryKeys } from '../campaignsQueryKeys';
import { AdCampaignEditFormValues, AdCampaignFormValues } from '../types';
import {
  telemetryNewCampaignDataCreateCampaign,
  telemetryUpdateCampaignDataUpdateCampaign,
} from '../utils/telemetry/telemetry';
import {
  transformAdCampaignFormFieldsToApi,
  transformAdCampaignFormFieldsToUpdateApi,
} from '../utils/transformFormToApi/transformAdCampaignFormToApi';

type UseAdCampaignMutationsProps = {
  venueIds: string[];
};

export const useAdCampaignMutations = ({ venueIds }: UseAdCampaignMutationsProps) => {
  const history = useHistory();
  const campaignsRoutes = useCampaignsRoutes();
  const queryClient = useQueryClient();

  const create = useMutation(
    (values: AdCampaignFormValues & { country: Country; currency: Currency }) => {
      const props = transformAdCampaignFormFieldsToApi({ ...values });
      return createAdCampaign(venueIds[0], props);
    },
    {
      onSuccess: async (res: AdCampaignResponse) => {
        toast.success(<FormattedMessage id="campaigns.success-create" />);

        const startDateObj = DateTime.fromISO(res.start_date);
        const endDateObj = res.end_date ? DateTime.fromISO(res.end_date) : undefined;
        const campaignDuration = endDateObj ? endDateObj.diff(startDateObj, 'days').days : -9999; // Confirmed with Abhinaya that when campaign is running forever, just set to -9999

        telemetryNewCampaignDataCreateCampaign({
          campaignType: CampaignType.ADS,
          campaignDuration,
        });

        await queryClient.invalidateQueries(CampaignsQueryKeys.GET_ALL_ADS(venueIds[0]));
        history.push(`${campaignsRoutes.details(res.id)}?campaignType=Ads`);
      },
      onError: () => {
        toast.error(<FormattedMessage id="campaigns.error-create" />);
      },
    },
  );

  const update = useMutation(
    (values: AdCampaignEditFormValues & { country: Country; currency: Currency }) => {
      const props = transformAdCampaignFormFieldsToUpdateApi(values);
      return patchAdCampaign(venueIds[0], values.campaignId, props);
    },
    {
      onSuccess: async (res: AdCampaignResponse) => {
        toast.success(<FormattedMessage id="campaigns.success-update" />);
        await queryClient.invalidateQueries(CampaignsQueryKeys.GET_SINGLE_ADS(venueIds[0], res.id));

        telemetryUpdateCampaignDataUpdateCampaign({
          campaignType: CampaignType.ADS,
        });

        history.push(`${campaignsRoutes.details(res.id)}?campaignType=Ads`);
      },
      onError: () => {
        toast.error(<FormattedMessage id="campaigns.error-update" />);
      },
    },
  );

  const stop = useMutation((campaignId: string) => stopAdCampaign(venueIds[0], campaignId), {
    onSuccess: async (campaignId: string) => {
      toast.success(<FormattedMessage id="campaigns.success-stop" />);

      await queryClient.invalidateQueries(CampaignsQueryKeys.GET_ALL_ADS(venueIds[0]));
      await queryClient.invalidateQueries(CampaignsQueryKeys.GET_SINGLE_ADS(venueIds[0], campaignId));
    },
    onError: () => {
      toast.error(<FormattedMessage id="campaigns.error-stop" />);
    },
  });

  const restart = useMutation((campaignId: string) => startAdCampaign(venueIds[0], campaignId), {
    onSuccess: async (campaignId: string) => {
      toast.success(<FormattedMessage id="campaigns.success-start" />);

      await queryClient.invalidateQueries(CampaignsQueryKeys.GET_ALL_ADS(venueIds[0]));
      await queryClient.invalidateQueries(CampaignsQueryKeys.GET_SINGLE_ADS(venueIds[0], campaignId));
    },
    onError: () => {
      toast.error(<FormattedMessage id="campaigns.error-start" />);
    },
  });

  return { create, restart, stop, update };
};
